'use client';

import { useId } from 'react';

import { COOKIES_COPY } from '@eliadmin/core/src/components/dlz/cookies/cookies-copy';
import { CookieIcon } from '@eliadmin/core/src/components/dlz/cookies/icons';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Red DLZ/Body/S Medium text action; the padding makes the 20 px line a 44 px touch target. */
const TEXT_ACTION =
  'dlz-body-s-medium -mx-1 -my-3 rounded-sm px-1 py-3 text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]';

type CookieBarProps = {
  /** Cookie policy page from the administration; without it the link is not rendered. */
  policyHref?: string;
  /**
   * Transparent while the settings dialog covers the page (Figma shows no bar there). It stays
   * rendered and focusable, so closing the dialog returns the focus to „Nastavenia“; the dialog
   * keeps it out of reach meanwhile (focus trap, `aria-hidden` siblings).
   */
  concealed: boolean;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onOpenSettings: () => void;
};

/**
 * Figma „Cookie banner“ 60326:22711 / „Cookie banner · Mobile“ 60370:44022: a white card floating
 * 50 px above the bottom edge in the container (one row from `lg`), on phones 12 px from the edges
 * over the mobile navbar with the buttons stacked („Prijať všetky“ first). It does not block the
 * page. Below `lg` (no Figma between 390 and 1024) the actions wrap under the text, right-aligned.
 */
export function CookieBar({
  policyHref,
  concealed,
  onAcceptAll,
  onRejectAll,
  onOpenSettings,
}: CookieBarProps) {
  const { t } = useTranslations();
  const titleId = useId();
  const textId = useId();

  return (
    <section
      aria-labelledby={titleId}
      aria-describedby={textId}
      className={cn(
        // Below `lg` over the mobile navbar (z-40), under the menu and search (z-50); from `lg` under
        // the header layer (z-40: catalogue drawer, search results). Dialogs are z-[200].
        'fixed inset-x-3 bottom-3 z-[45] mx-auto flex max-w-[1328px] flex-col gap-6 rounded-[18px] bg-white p-4 shadow-card-hover',
        'md:inset-x-6 md:flex-row md:flex-wrap md:items-center md:gap-x-8 md:px-8 md:py-6',
        'lg:inset-x-8 lg:bottom-[50px] lg:z-[35] lg:flex-nowrap',
        concealed && 'pointer-events-none opacity-0',
      )}
    >
      <CookieIcon className="shrink-0 text-[#D7191F]" />
      <div className="flex flex-col items-start gap-4 md:min-w-0 md:flex-[1_1_calc(100%_-_72px)] lg:flex-[1_1_0%]">
        <div className="flex flex-col gap-2">
          <h2 id={titleId} className="dlz-h5 text-[#0F1012]">
            {t('text275')}
          </h2>
          <p id={textId} className="dlz-body-s text-[#4F545C]">
            {COOKIES_COPY.text}
          </p>
        </div>
        {policyHref ? (
          <Link href={policyHref} prefetch={false} className={TEXT_ACTION}>
            {COOKIES_COPY.policy}
          </Link>
        ) : null}
      </div>
      {/* DOM order = desktop order (left to right); phones show it bottom-up. */}
      <div className="flex flex-col-reverse items-center gap-3 md:ml-auto md:flex-row lg:ml-0">
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={onOpenSettings}
          className={TEXT_ACTION}
        >
          {t('text855')}
        </button>
        <Button
          type="button"
          variant="secondary"
          onClick={onRejectAll}
          className="h-12 w-full md:h-[52px] md:w-auto"
        >
          {COOKIES_COPY.rejectAll}
        </Button>
        <Button type="button" onClick={onAcceptAll} className="h-12 w-full md:h-[52px] md:w-auto">
          {t('text2079')}
        </Button>
      </div>
    </section>
  );
}
